import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";

const ABOUT_IMG = "/founder.jpg";

const CHAPTERS = [
  {
    num: "01",
    title: "Rooted in science",
    body: "Every plan begins with your clinical picture — labs, history and symptoms. No fads, no guesswork. Just protocols supported by peer-reviewed nutrition research.",
  },
  {
    num: "02",
    title: "Restored through the gut",
    body: "The gut is where healing compounds. We rebuild the microbiome, calm inflammation and repair digestion as the foundation for whole-body wellness.",
  },
  {
    num: "03",
    title: "Guided with empathy",
    body: "Chronic illness is exhausting. You get a partner who listens, adjusts and walks with you — not a rigid template you're left to decode alone.",
  },
];

const CREDS = [
  "Registered Clinical Dietitian (MSc Nutrition)",
  "Certified in Medical Nutrition Therapy",
  "Advanced training in FODMAP & SIBO protocols",
  "Integrative & functional nutrition practitioner",
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-28 md:pt-36">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10">
        <p className="font-sans text-xs tracking-[0.4em] uppercase text-[#7D8C7A] mb-8">
          About the practice
        </p>
        <h1 className="font-serif text-[#2C3C30] text-6xl md:text-8xl lg:text-[8vw] leading-[0.9] tracking-tighter">
          <MaskedLines
            lines={["Clinical rigour,", <em key="e" className="text-[#7D8C7A]">human warmth.</em>]}
          />
        </h1>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
        <div className="md:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            whileInView={{ clipPath: "inset(0 0 0% 0)" }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-b-[8rem] overflow-hidden bg-white"
          >
            <img
              src={ABOUT_IMG}
              alt="Founder of Adya Nutrition"
              className="w-full h-[560px] object-cover object-top bg-white"
            />
          </motion.div>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          <Reveal>
            <p className="font-sans text-xs tracking-[0.4em] uppercase text-[#7D8C7A] mb-3">
              Your nutritionist
            </p>
            <h2 className="font-serif text-5xl md:text-6xl text-[#2C3C30] tracking-tight leading-none">
              Dt. Darsh Tank
            </h2>
            <p className="font-sans text-sm text-[#566158] mt-3 mb-8">
              Clinical &amp; Integrative Nutritionist &middot; 5+ years of experience
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="font-serif text-3xl md:text-4xl leading-snug text-[#2C3C30] tracking-tight">
              I&rsquo;m a clinical nutritionist who believes the gut is the
              quiet centre of your health.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="font-sans text-[#566158] leading-relaxed space-y-5 mt-8 text-lg">
              <p>
                After years watching patients cycle through generic advice and
                temporary fixes, I built a practice that combines evidence-based
                Medical Nutrition Therapy with integrative, holistic gut
                restoration.
              </p>
              <p>
                Whether you&rsquo;re managing IBS, IBD, GERD, SIBO, a metabolic
                condition or an autoimmune flare, my work starts with truly
                understanding your body — then designing nutrition that is
                precise, sustainable and unmistakably yours.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <ul className="mt-10 space-y-3">
              {CREDS.map((c) => (
                <li
                  key={c}
                  className="flex items-start gap-3 font-sans text-[#2C3C30]"
                >
                  <CheckCircle2
                    size={20}
                    className="text-[#7D8C7A] mt-0.5 shrink-0"
                  />
                  {c}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Numbered manifesto chapters */}
      <section className="bg-[#2C3C30] text-[#F7F6F2] py-24 md:py-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <h2 className="font-serif text-5xl md:text-7xl tracking-tight mb-16 md:mb-24">
              The philosophy
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.12}>
                <div data-testid={`chapter-${c.num}`}>
                  <span className="font-serif text-6xl text-[#7D8C7A]">
                    {c.num}
                  </span>
                  <div className="h-px w-full bg-[#F7F6F2]/20 my-6" />
                  <h3 className="font-serif text-3xl tracking-tight mb-4">
                    {c.title}
                  </h3>
                  <p className="font-sans text-[#D1D6CC] leading-relaxed">
                    {c.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="about-book-cta"
              className="inline-flex mt-20 items-center gap-3 rounded-full bg-[#F7F6F2] px-8 py-4 text-[#2C3C30] font-medium transition-colors duration-300 hover:bg-[#D1D6CC]"
            >
              Book a Consultation
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
